import { getConsultationsExcel, getConsultationsPdf } from "@/api/edit-comunications/edit-comunications";
import { KPICards } from "@/components/kpi-cards";
import { Button } from "@/components/ui/button";
import { getColumns } from "@/components/ui/table/columns/communications"; // Importamos getColumns
import { priorities } from "@/components/ui/table/data-table-filters.jsx";
import { DataTable } from "@/components/ui/table/data-table.jsx";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { CircleHelp } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Loader from "@/components/utils/loader";
import { calculateKpisFromCounts } from "@/lib/utils";
import {
  buildPageParams,
  createCommunicationPageLoader,
  computePageAfterDelete,
  DEFAULT_PAGE_SIZE,
  extractServerFilters,
} from "@/lib/communication-listing";
import { useStore } from "@/store";
import { CloudDownload, Plus, FileText } from "lucide-react";
import { UndoDot } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCommunicationsPage } from "@/api/client/communications"; // Petición paginada al listado
import { AlertCircle } from "lucide-react";

const QueriesPage = () => {
  const selectedLanguage = useStore((state) => state.selectedLanguage);
  const { t } = useTranslation();
  const [filterState, setFilterState] = useState(null);
  const [rows, setRows] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [countsByState, setCountsByState] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sorting, setSorting] = useState([]);
  const [rawSearch, setRawSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedRisk, setAppliedRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const listingLoaderRef = useRef(null);
  if (!listingLoaderRef.current) {
    listingLoaderRef.current = createCommunicationPageLoader(getCommunicationsPage);
  }
  const [countsSource, setCountsSource] = useState(null);
  const pageRequestRef = useRef(0);
  const countsRequestRef = useRef(0);
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const states = useStore((state) => state.states) || [];

  const consultationsCardTypes = useMemo(() => {
    try {
      const consultationsCard = states?.filter(
        (state) => state?.type === "consultation"
      ) || [];

      return consultationsCard.map((item) => ({
        id: item?.id || "",
        name:
          item?.names?.find((name) => name?.language === selectedLanguage)?.text ||
          item?.names?.[0]?.text ||
          t("general.notAvailable"),
        color: item?.color || "#cccccc",
      }));
    } catch (err) {
      console.error("Error al procesar cardTypes:", err);
      return [];
    }
  }, [states, selectedLanguage, t]);

  // Búsqueda con debounce: la tabla notifica cada tecla, la petición espera.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(rawSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [rawSearch]);

  const fetchPage = async (overrides = {}) => {
    const current = { ...paginationRef.current, ...overrides };
    const params = buildPageParams({
      type: "consultation",
      pageIndex: current.pageIndex,
      pageSize: current.pageSize,
      search: appliedSearch,
      stateId: filterState,
      risk: appliedRisk,
      sorting,
    });
    const requestId = (pageRequestRef.current += 1);
    setLoading(true);
    try {
      const response = await listingLoaderRef.current.load(params);
      // Ignorar respuestas obsoletas tras cambios rápidos.
      if (pageRequestRef.current !== requestId) {
        return;
      }
      const payload = response?.data || {};
      const pageRows = Array.isArray(payload.data) ? payload.data : [];
      setCountsSource((previous) => {
        if (!payload.countsByState && previous &&
            previous.search === appliedSearch && previous.risk === appliedRisk) return previous;
        return { payload, search: appliedSearch, risk: appliedRisk };
      });
      const pagePagination = payload.pagination || {};
      const pageTotal = pagePagination.total ?? 0;
      const pages = pagePagination.totalPages ?? 0;
      setRows(pageRows);
      setQuestions(Array.isArray(payload.questions) ? payload.questions : []);
      setTotal(pageTotal);
      setPageCount(pages);
      setError(null);
      if (
        pageRows.length === 0 &&
        pageTotal > 0 &&
        current.pageIndex > 0 &&
        current.pageIndex >= pages
      ) {
        const steppedBack = computePageAfterDelete({
          total: pageTotal,
          pageSize: current.pageSize,
          pageIndex: current.pageIndex,
          deletedCount: 0,
        });
        setPagination((prev) => ({ ...prev, pageIndex: steppedBack.pageIndex }));
      }
    } catch (err) {
      if (pageRequestRef.current !== requestId) {
        return;
      }
      console.error("Error con la carga", err);
      setError(t("errors.loadingQueries"));
    } finally {
      if (pageRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  const fetchCounts = async () => {
    if (!countsSource) return;
    if (countsSource.payload.countsByState) {
      countsRequestRef.current += 1;
      setCountsByState(countsSource.payload.countsByState);
      return;
    }
    const base = {
      type: "consultation",
      page: 1,
      pageSize: 1,
      search: countsSource.search,
      stateId: null,
      risk: countsSource.risk,
      orderField: null,
      orderDirection: null,
    };
    const requestId = (countsRequestRef.current += 1);
    try {
      const responses = await Promise.all([
        ...consultationsCardTypes.map((cardType) =>
          getCommunicationsPage({ ...base, stateId: cardType.id }),
        ),
      ]);
      if (countsRequestRef.current !== requestId) {
        return;
      }
      const nextCounts = {};
      responses.forEach((response, index) => {
        const cardType = consultationsCardTypes[index];
        if (cardType) {
          nextCounts[cardType.id] = response?.data?.pagination?.total ?? 0;
        }
      });
      setCountsByState(nextCounts);
    } catch (err) {
      if (countsRequestRef.current !== requestId) {
        return;
      }
      console.error("Error con el cálculo de KPIs:", err);
    }
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, appliedSearch, appliedRisk, filterState, JSON.stringify(sorting)]);

  useEffect(() => {
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countsSource, JSON.stringify(consultationsCardTypes.map((card) => card.id))]);

  // Refrescar solo la página actual tras acciones (bloquear, borrar, ...).
  useEffect(() => {
    const handler = () => {
      listingLoaderRef.current.clear();
      setCountsSource(null);
      fetchPage();
    };
    window.addEventListener("communications:refresh", handler);
    return () => {
      window.removeEventListener("communications:refresh", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch, appliedRisk, filterState, sorting]);

  const inquiriesKpis = useMemo(() => {
    try {
      return calculateKpisFromCounts(countsByState || {}, consultationsCardTypes || []);
    } catch (err) {
      console.error("Error al calcular KPIs:", err);
      return [];
    }
  }, [countsByState, consultationsCardTypes]);

  const handleCardClick = (id) => {
    setFilterState(id);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleReset = () => {
    setFilterState(null);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handlePaginationChange = (next) => {
    const current = paginationRef.current;
    const merged = { ...current, ...next };
    if (
      merged.pageIndex !== current.pageIndex ||
      merged.pageSize !== current.pageSize
    ) {
      setLoading(true);
    }
    setPagination((prev) => ({ ...prev, ...next }));
  };

  const handleSortingChange = (nextSorting) => {
    setSorting(nextSorting);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleFiltersChange = (nextFilters) => {
    const { search, risk } = extractServerFilters(nextFilters);
    setRawSearch(search);
    setAppliedRisk(risk);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // Generar columnas utilizando getColumns para las columnas dinámicas
  const columns = useMemo(() => {
    try {
      return getColumns(rows || [], selectedLanguage, questions);
    } catch (err) {
      console.error("Error al generar columnas:", err);
      return [];
    }
  }, [rows, selectedLanguage, questions]);

  const handleDownloadExcel = async () => {
    try {
      const data = await getConsultationsExcel();
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "consultas.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error al descargar el archivo Excel:", error);
      setError(t("errors.downloadingExcel"));
      // Limpiar el error después de 5 segundos
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const data = await getConsultationsPdf();
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "consultas.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error al descargar el archivo PDF:", error);
      setError(t("errors.downloadingPdf"));
      // Limpiar el error después de 5 segundos
      setTimeout(() => setError(null), 5000);
    }
  };

  // Mostrar mensaje de error si hay un error y no hay datos
  if (!loading && error && (!rows || rows.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t("errors.errorTitle")}</h2>
        <p className="text-gray-600">{error}</p>
        <Button 
          className="mt-4" 
          onClick={() => window.location.reload()}
        >
          {t("buttonTexts.retry")}
        </Button>
      </div>
    );
  }

  return (
    <>
      {loading && (
        <div role="status" aria-label={t("general.loading")} className="flex justify-center h-screen">
          <Loader style={"loader"} />
        </div>
      )}
      {/* Conservar la tabla montada mantiene sus filtros y ordenación al recargar. */}
    <div hidden={loading} aria-busy={loading} className="space-y-4 sm:space-y-6 lg:space-y-10">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      <section className="flex flex-col gap-6">
        <h1>{t("MainLayout.queriesOverviewTitle")}</h1>
        <KPICards
          data={inquiriesKpis}
          onCardClick={handleCardClick}
          cardEntity={{ fontColor: "#ffffff" }}
        />
      </section>
      <section className="flex flex-col gap-6">
        <div className="flex items-center">
          <h1 className="mr-2">{t("MainLayout.queriesHeaderTitle")}</h1>
          {filterState !== null && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    className="bg-primary rounded-full"
                    onClick={handleReset}
                  >
                    <UndoDot />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("tooltip.clearFilter")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <NavLink
            className={cn(buttonVariants({ variant: "default", size: "sm" }))}
            to="/admin/queries/create" // Nueva ruta dentro del panel de control
          >
            <CircleHelp className="h-6 w-6 mr-2" />
            <h3 className="text-base">{t("buttonTexts.createQuery")}</h3>
          </NavLink>
          <div className="ml-auto flex w-full md:w-fit gap-2">
            <Button
              type="button"
              className="w-full md:w-fit flex gap-1.5 items-center"
              icon={CloudDownload}
              onClick={handleDownloadExcel}
            >
              {t("buttonTexts.recordBook")}
            </Button>
            <Button
              type="button"
              className="w-full md:w-fit flex gap-1.5 items-center"
              icon={FileText}
              onClick={handleDownloadPdf}
            >
              {t("buttonTexts.recordBookPdf")}
            </Button>
          </div>
        </div>
        <DataTable
          data={rows}
          columns={columns} // Usamos columns generado dinámicamente
          mainFilterColumn="identificationNumber"
          filterOptions={[
            {
              column: "risk",
              title: t("headColumns.communications.priority"),
              options: priorities,
            },
          ]}
          textPlaceholder={t("MainLayout.consultationPageOverview.filterText")}
          server={{
            pagination,
            pageCount,
            onPaginationChange: handlePaginationChange,
            onSortingChange: handleSortingChange,
            onFiltersChange: handleFiltersChange,
          }}
        />
      </section>
    </div>
    </>
  );
};

export default QueriesPage;
